import React from 'react';
import { Box, Button, ButtonProps } from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';

import { CandidateDetailsAction, hasPermission } from '@constants';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { selectPermissions } from '@redux/accounts';
import { markAvailableCandidate } from '@redux/availableCandidates';
import { AvailableCandidateMark, IAvailableCandidate } from '@types';
import { SHOW_CANDIDATE_MARKS, offersRequest, toggledMark } from '@utils';
import { buttonSizeSx, cardButtonSx } from 'theme/buttons';
import {
  ACTION_SX,
  CARD_SECONDARY_ACTION_SX,
  ClosedStatus,
  ListActionButton,
  PendingStatus,
  SECONDARY_ACTION_SX,
} from 'components/candidate/CandidatesList/components/ListActionParts';
import {
  BarNote,
  DECISION_SX,
} from 'components/candidate/CandidaterDetailsActionButton/ActionBar';

type AvailableActionsVariant = 'row' | 'card' | 'bar';

const PRIMARY_SX = {
  row: ACTION_SX,
  card: cardButtonSx,
  bar: buttonSizeSx.md,
};

const SECONDARY_SX = {
  row: SECONDARY_ACTION_SX,
  card: CARD_SECONDARY_ACTION_SX,
  bar: DECISION_SX,
};

const MARK_SX = {
  row: { minWidth: buttonSizeSx.sm.minHeight, fontSize: '1rem' },
  card: { minWidth: buttonSizeSx.md.minHeight, fontSize: '1.125rem' },
  bar: { minWidth: buttonSizeSx.md.minHeight, fontSize: '1.125rem' },
};

const GAP = { row: 0.75, card: 0.5, bar: 1 };

const MARK_HINTS = {
  favourite: ['Add to your favourites', 'Remove from your favourites'],
  skip: ['Skip candidate', 'Undo skip'],
};

const SETTING_UP = 'Setting up interview';

interface IAvailableCandidateActionsProps {
  candidate: IAvailableCandidate;
  variant: AvailableActionsVariant;
  onAction: (candidateId: string, action: CandidateDetailsAction) => void;
  onLearnMore: (candidateId: string) => void;
}

export const AvailableCandidateActions: React.FC<
  IAvailableCandidateActionsProps
> = ({ candidate, variant, onAction, onLearnMore }) => {
  const dispatch = useAppDispatch();
  const readOnly = !hasPermission(useAppSelector(selectPermissions), 'N:W');
  const withIcon = variant !== 'card';

  if (!offersRequest(candidate, 'interview'))
    return variant === 'bar' ? (
      <BarNote icon>{SETTING_UP}</BarNote>
    ) : (
      <PendingStatus
        label={SETTING_UP}
        hint="AgileEngine is setting up the interview with the candidate"
      />
    );

  const action = (hint: string, run: () => void, button: ButtonProps) => {
    const onClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      run();
    };

    return variant === 'bar' ? (
      <Button disabled={readOnly} onClick={onClick} {...button} />
    ) : (
      <ListActionButton
        hint={hint}
        readOnly={readOnly}
        onClick={onClick}
        {...button}
      />
    );
  };

  const markToggle = (
    mark: AvailableCandidateMark,
    run: () => void,
    icon: React.ReactElement,
    pressedIcon: React.ReactElement,
  ) => {
    const pressed = candidate.mark === mark;
    const hint = MARK_HINTS[mark][pressed ? 1 : 0];

    return (
      <ListActionButton
        hint={hint}
        readOnly={readOnly}
        aria-label={hint}
        aria-pressed={pressed}
        variant="outlined"
        onClick={(event) => {
          event.stopPropagation();
          run();
        }}
        sx={{
          ...SECONDARY_SX[variant],
          ...MARK_SX[variant],
          px: 0,
          ...(pressed && { color: 'text.link' }),
        }}
      >
        {pressed ? pressedIcon : icon}
      </ListActionButton>
    );
  };

  const actions = (
    <>
      {action(
        'Books a slot. AgileEngine confirms it with the candidate',
        () => onAction(candidate.id, CandidateDetailsAction.SCHEDULE_INTERVIEW),
        {
          variant: 'contained',
          startIcon: <EventOutlinedIcon />,
          sx: PRIMARY_SX[variant],
          children: 'Schedule interview',
        },
      )}
      {offersRequest(candidate, 'learnMore') ? (
        action(
          'Asks your AgileEngine team a question about this candidate',
          () => onLearnMore(candidate.id),
          {
            variant: 'outlined',
            startIcon: withIcon ? <HelpOutlineIcon /> : undefined,
            sx: SECONDARY_SX[variant],
            children: 'Ask a question',
          },
        )
      ) : (
        <ClosedStatus
          label="Team notified"
          hint="Your AgileEngine team will get back to you about this candidate"
          settled
        />
      )}
      {SHOW_CANDIDATE_MARKS && (
        <Box display="flex" gap={GAP[variant]}>
          {markToggle(
            'favourite',
            () =>
              dispatch(
                markAvailableCandidate({
                  candidateId: candidate.id,
                  mark: toggledMark(candidate, 'favourite'),
                }),
              ),
            <StarBorderIcon fontSize="inherit" />,
            <StarIcon fontSize="inherit" />,
          )}
          {markToggle(
            'skip',
            () => onAction(candidate.id, CandidateDetailsAction.SKIP_CANDIDATE),
            <ThumbDownOutlinedIcon fontSize="inherit" />,
            <ThumbDownIcon fontSize="inherit" />,
          )}
        </Box>
      )}
    </>
  );

  if (variant === 'bar') return actions;

  return (
    <Box display="flex" alignItems="center" gap={GAP[variant]} flexWrap="wrap">
      {actions}
    </Box>
  );
};
